<template>
  <div class="page-card">
    <div class="section-header">
      <div class="section-title">服务项目列表</div>
      <div class="section-actions">
        <button class="add-btn" type="button" @click="openAddModal">+ 新增项目</button>
      </div>
    </div>

    <div v-if="listLoading" class="list-loading">加载中...</div>
    <table v-else class="service-table">
      <thead>
        <tr>
          <th class="col-code">编号</th>
          <th>项目名称</th>
          <th>描述</th>
          <th class="col-duration">预计耗时</th>
          <th class="col-devices">所需设备</th>
          <th class="col-action">操作</th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="!rows.length">
          <td colspan="6" class="empty-cell">暂无数据</td>
        </tr>
        <tr v-for="item in rows" :key="item.id">
          <td class="cell-code">{{ item.code }}</td>
          <td class="cell-name">{{ item.name }}</td>
          <td class="cell-desc">{{ item.serviceDescribe }}</td>
          <td>{{ formatDurationDisplay(item.duration) }}</td>
          <td>
            <div class="device-tags">
              <span
                v-for="(tag, index) in item.devices"
                :key="item.id + '-' + index"
                class="device-tag device-tag--highlight"
              >
                {{ tag.name }}
              </span>
            </div>
          </td>
          <td class="col-action">
            <div class="action-btns">
              <button class="action-btn action-btn-edit" type="button" @click="editRow(item)">
                编辑
              </button>
              <button class="action-btn action-btn-delete" type="button" @click="deleteRow(item)">
                删除
              </button>
            </div>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 新增 / 编辑服务项目弹框 -->
    <div
      v-if="showProjectModal"
      class="modal-overlay"
      @click.self="closeProjectModal"
    >
      <div class="modal modal-service">
        <div class="modal-header">
          <span class="modal-title">{{ modalMode === 'edit' ? '编辑服务项目' : '新增服务项目' }}</span>
          <span class="modal-close" @click="closeProjectModal">×</span>
        </div>
        <div class="modal-body">
          <div v-if="projectDetailLoading" class="modal-detail-loading">加载项目信息中...</div>
          <template v-else>
          <div class="form-row form-row-2">
            <div class="form-group">
              <label class="form-label">项目编号</label>
              <input
                v-model.trim="projectForm.code"
                class="form-input"
                placeholder="如 S01"
              />
            </div>
            <div class="form-group">
              <label class="form-label">项目名称</label>
              <input
                v-model.trim="projectForm.name"
                class="form-input"
                placeholder="请输入项目名称"
              />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">项目描述</label>
            <input
              v-model.trim="projectForm.describe"
              class="form-input"
              placeholder="请输入项目描述"
            />
          </div>
          <div class="form-group">
            <label class="form-label">预计耗时</label>
            <div class="duration-input-wrap">
              <input
                v-model.trim="projectForm.duration"
                class="form-input duration-input"
                placeholder="如 2-3"
              />
              <span class="duration-suffix">（小时）</span>
            </div>
          </div>
          <div class="form-group field-required-switch-group">
            <label class="form-label">需要种植体信息的选上</label>
            <div class="field-required-switch-row">
              <el-switch
                v-model="projectForm.isFieldRequired"
                :active-value="1"
                :inactive-value="0"
              />
            </div>
          </div>
          <div class="form-group">
            <label class="form-label">所需设备（多选）</label>
            <div v-if="devicesLoading" class="device-check-loading">加载设备中...</div>
            <div v-else-if="!allDevices.length" class="device-check-empty">暂无设备</div>
            <div v-else class="device-check-list">
              <label
                v-for="device in allDevices"
                :key="device.deviceId"
                class="device-check-item"
              >
                <input
                  type="checkbox"
                  :value="device.deviceId"
                  v-model="projectForm.checkedDeviceIds"
                />
                <span class="device-check-name">{{ device.deviceName }}</span>
              </label>
            </div>
            <button
              class="repair-link-btn"
              type="button"
              @click="openRepairModal"
            >
              +关联修复产品
            </button>
            <div v-if="selectedRepairs.length" class="repair-selected-tags">
              <span
                v-for="repair in selectedRepairs"
                :key="'selected-repair-' + repair.repairId"
                class="repair-tag"
              >
                {{ repair.repairName }}
              </span>
            </div>
          </div>

          <div v-if="modalMode === 'edit'" class="form-group project-image-group">
            <label class="form-label">项目图片</label>

            <div v-if="projectImageLoading" class="project-image-loading">
              加载图片中...
            </div>

            <template v-else-if="hasProjectImage">
              <div class="project-image-viewer">
                <div class="project-image-thumb">
                  <img
                    class="project-image-thumb-img"
                    :src="projectImageInfo.imageUrl"
                    :alt="projectImageInfo.fileName || '项目图片'"
                  />
                  <div class="project-image-thumb-mask">
                    <i
                      class="el-icon-zoom-in"
                      title="放大查看"
                      @click="openProjectImagePreview"
                    ></i>
                    <i
                      class="el-icon-delete"
                      title="删除"
                      @click="removeProjectImage"
                    ></i>
                  </div>
                </div>
              </div>
              <div class="project-image-tip">
                支持 jpg / png / gif，大小不超过 2MB，仅可上传 1 张
              </div>
            </template>

            <template v-else>
              <div class="project-image-upload">
                <el-upload
                  action="#"
                  list-type="picture-card"
                  accept=".jpg,.jpeg,.png,.gif,image/jpeg,image/png,image/gif"
                  :limit="1"
                  :show-file-list="false"
                  :disabled="projectImageUploading"
                  :http-request="handleProjectImageUpload"
                  :before-upload="beforeProjectImageUpload"
                  :on-exceed="handleProjectImageExceed"
                >
                  <i v-if="!projectImageUploading" class="el-icon-plus"></i>
                  <i v-else class="el-icon-loading"></i>
                </el-upload>
              </div>
              <div class="project-image-tip">
                支持 jpg / png / gif，大小不超过 2MB，仅可上传 1 张
              </div>
            </template>
          </div>

          </template>
        </div>
        <div class="modal-footer">
          <button class="footer-btn footer-btn-cancel" type="button" @click="closeProjectModal">
            取消
          </button>
          <button
            class="footer-btn footer-btn-save"
            type="button"
            :disabled="savingProject || projectDetailLoading"
            @click="saveProject"
          >
            {{ savingProject ? "保存中..." : "保存" }}
          </button>
        </div>
      </div>
    </div>

    <el-dialog
      :visible.sync="projectImagePreviewVisible"
      title="图片预览"
      width="720px"
      append-to-body
      custom-class="project-image-preview-dialog"
      @closed="resetProjectImagePreviewTransform"
    >
      <div class="project-image-preview-tip">滚轮缩放，按住拖动可移动</div>
      <div
        class="project-image-preview-stage"
        @wheel.prevent="onProjectImagePreviewWheel"
        @mousedown.prevent="onProjectImagePreviewMouseDown"
      >
        <img
          v-if="projectImagePreviewUrl"
          class="project-image-preview"
          :src="projectImagePreviewUrl"
          alt="项目图片预览"
          :style="projectImagePreviewStyle"
          draggable="false"
        />
      </div>
    </el-dialog>

    <!-- 关联修复产品弹框 -->
    <div
      v-if="showRepairModal"
      class="modal-overlay repair-modal-overlay"
      @click.self="closeRepairModal"
    >
      <div class="modal modal-repair-select">
        <div class="modal-header">
          <span class="modal-title">关联修复产品</span>
          <span class="modal-close" @click="closeRepairModal">×</span>
        </div>
        <div class="modal-body">
          <div v-if="repairsLoading" class="repair-check-loading">加载修复产品中...</div>
          <div v-else-if="!allRepairs.length" class="repair-check-empty">暂无修复产品</div>
          <div v-else class="repair-check-list">
            <label
              v-for="repair in allRepairs"
              :key="repair.repairId"
              class="repair-check-item"
            >
              <input
                type="checkbox"
                :value="repair.repairId"
                v-model="projectForm.checkedRepairIds"
              />
              <span class="repair-check-name">{{ repair.repairName }}</span>
            </label>
          </div>
        </div>
        <div class="modal-footer">
          <button class="footer-btn footer-btn-cancel" type="button" @click="closeRepairModal">
            取消
          </button>
          <button class="footer-btn footer-btn-save" type="button" @click="saveRepairModal">
            保存
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  addProjects,
  deleteProject,
  deleteProjectImage,
  getProject,
  getProjectImage,
  listAllDevices,
  listProjectsWithDevices,
  updateProject,
  uploadProjectImage,
} from "@/api/device";
import { listAllRepairs } from "@/api/repair";

const SUCCESS_CODE = 20000;
const PROJECT_IMAGE_MAX_SIZE = 2 * 1024 * 1024;
const PROJECT_IMAGE_ACCEPT = ["image/jpeg", "image/png", "image/gif"];

function createEmptyImageTransform() {
  return {
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    dragging: false,
    dragStartX: 0,
    dragStartY: 0,
    originOffsetX: 0,
    originOffsetY: 0,
  };
}

export default {
  name: "ServicePage",
  data() {
    return {
      listLoading: false,
      showProjectModal: false,
      modalMode: "add",
      editingProjectId: null,
      projectDetailLoading: false,
      savingProject: false,
      devicesLoading: false,
      repairsLoading: false,
      showRepairModal: false,
      allDevices: [],
      allRepairs: [],
      projectForm: {
        code: "",
        name: "",
        describe: "",
        duration: "",
        isFieldRequired: 0,
        checkedDeviceIds: [],
        checkedRepairIds: [],
      },
      projectImageFileList: [],
      projectImageInfo: null,
      projectImageLoading: false,
      projectImageUploading: false,
      projectImageRemoving: false,
      projectImagePreviewVisible: false,
      projectImagePreviewUrl: "",
      projectImageInlineTransform: createEmptyImageTransform(),
      projectImagePreviewTransform: createEmptyImageTransform(),
      rows: [],
    };
  },
  computed: {
    hasProjectImage() {
      return Boolean(
        this.projectImageInfo && String(this.projectImageInfo.imageUrl || "").trim()
      );
    },
    projectImageInlineStyle() {
      const t = this.projectImageInlineTransform;
      return {
        transform: `translate(${t.offsetX}px, ${t.offsetY}px) scale(${t.scale})`,
        cursor: t.dragging ? "grabbing" : t.scale > 1 ? "grab" : "default",
      };
    },
    projectImagePreviewStyle() {
      const t = this.projectImagePreviewTransform;
      return {
        transform: `translate(${t.offsetX}px, ${t.offsetY}px) scale(${t.scale})`,
        cursor: t.dragging ? "grabbing" : t.scale > 1 ? "grab" : "default",
      };
    },
    selectedRepairs() {
      return (this.projectForm.checkedRepairIds || [])
        .map((id) => {
          const repairId = String(id);
          const repair = this.allRepairs.find((item) => item.repairId === repairId);
          return repair
            ? { repairId, repairName: repair.repairName }
            : { repairId, repairName: repairId };
        })
        .filter((item) => item.repairId);
    },
  },
  mounted() {
    this.fetchProjectList();
  },
  beforeDestroy() {
    this.unbindImageDrag("inline");
    this.unbindImageDrag("preview");
  },
  methods: {
    showMessage(type, text) {
      this.$message({ message: text, type });
    },
    isBizError(res) {
      return res && res.code != null && Number(res.code) !== SUCCESS_CODE;
    },
    resetProjectForm() {
      this.projectForm = {
        code: "",
        name: "",
        describe: "",
        duration: "",
        isFieldRequired: 0,
        checkedDeviceIds: [],
        checkedRepairIds: [],
      };
      this.resetProjectImage();
    },
    resetProjectImage() {
      this.projectImageFileList = [];
      this.projectImageInfo = null;
      this.projectImageLoading = false;
      this.projectImageUploading = false;
      this.projectImageRemoving = false;
      this.projectImagePreviewVisible = false;
      this.projectImagePreviewUrl = "";
      this.projectImageInlineTransform = createEmptyImageTransform();
      this.resetProjectImagePreviewTransform();
    },
    resetProjectImagePreviewTransform() {
      this.projectImagePreviewTransform = createEmptyImageTransform();
      this.unbindImageDrag("preview");
    },
    resetProjectImageInlineTransform() {
      this.projectImageInlineTransform = createEmptyImageTransform();
      this.unbindImageDrag("inline");
    },
    getImageTransform(kind) {
      return kind === "inline"
        ? this.projectImageInlineTransform
        : this.projectImagePreviewTransform;
    },
    onImageWheel(kind, event) {
      const transform = this.getImageTransform(kind);
      const delta = event.deltaY > 0 ? -0.1 : 0.1;
      const next = Math.min(
        5,
        Math.max(0.4, Number((transform.scale + delta).toFixed(2)))
      );
      transform.scale = next;
      if (next <= 1) {
        transform.offsetX = 0;
        transform.offsetY = 0;
      }
    },
    onImageMouseDown(kind, event) {
      const transform = this.getImageTransform(kind);
      if (transform.scale <= 1) return;
      transform.dragging = true;
      transform.dragStartX = event.clientX;
      transform.dragStartY = event.clientY;
      transform.originOffsetX = transform.offsetX;
      transform.originOffsetY = transform.offsetY;
      const moveHandler =
        kind === "inline"
          ? this.onProjectImageInlineMouseMove
          : this.onProjectImagePreviewMouseMove;
      const upHandler =
        kind === "inline"
          ? this.onProjectImageInlineMouseUp
          : this.onProjectImagePreviewMouseUp;
      window.addEventListener("mousemove", moveHandler);
      window.addEventListener("mouseup", upHandler);
    },
    onImageMouseMove(kind, event) {
      const transform = this.getImageTransform(kind);
      if (!transform.dragging) return;
      transform.offsetX =
        transform.originOffsetX + (event.clientX - transform.dragStartX);
      transform.offsetY =
        transform.originOffsetY + (event.clientY - transform.dragStartY);
    },
    onImageMouseUp(kind) {
      const transform = this.getImageTransform(kind);
      transform.dragging = false;
      this.unbindImageDrag(kind);
    },
    unbindImageDrag(kind) {
      if (kind === "inline") {
        window.removeEventListener("mousemove", this.onProjectImageInlineMouseMove);
        window.removeEventListener("mouseup", this.onProjectImageInlineMouseUp);
        return;
      }
      window.removeEventListener("mousemove", this.onProjectImagePreviewMouseMove);
      window.removeEventListener("mouseup", this.onProjectImagePreviewMouseUp);
    },
    onProjectImageInlineWheel(event) {
      this.onImageWheel("inline", event);
    },
    onProjectImageInlineMouseDown(event) {
      this.onImageMouseDown("inline", event);
    },
    onProjectImageInlineMouseMove(event) {
      this.onImageMouseMove("inline", event);
    },
    onProjectImageInlineMouseUp() {
      this.onImageMouseUp("inline");
    },
    onProjectImagePreviewWheel(event) {
      this.onImageWheel("preview", event);
    },
    onProjectImagePreviewMouseDown(event) {
      this.onImageMouseDown("preview", event);
    },
    onProjectImagePreviewMouseMove(event) {
      this.onImageMouseMove("preview", event);
    },
    onProjectImagePreviewMouseUp() {
      this.onImageMouseUp("preview");
    },
    applyProjectImageData(data) {
      if (!data || typeof data !== "object") {
        this.projectImageInfo = null;
        this.projectImageFileList = [];
        this.resetProjectImageInlineTransform();
        return;
      }
      const imageUrl = String(
        data.imageUrl ?? data.projectImageUrl ?? data.url ?? ""
      ).trim();
      if (!imageUrl) {
        this.projectImageInfo = null;
        this.projectImageFileList = [];
        this.resetProjectImageInlineTransform();
        return;
      }
      const imageId = String(data.imageId ?? "").trim();
      const fileName =
        String(data.fileName ?? data.imageName ?? "").trim() || "项目图片";
      this.projectImageInfo = {
        imageId,
        projectId: String(data.projectId ?? this.editingProjectId ?? "").trim(),
        imageUrl,
        fileName,
      };
      this.projectImageFileList = [
        {
          name: fileName,
          url: imageUrl,
          uid: imageId || `project-image-${Date.now()}`,
          status: "success",
        },
      ];
      this.resetProjectImageInlineTransform();
    },
    async fetchProjectImage(projectId) {
      const id = String(projectId || this.editingProjectId || "").trim();
      if (!id) {
        this.applyProjectImageData(null);
        return;
      }
      this.projectImageLoading = true;
      try {
        const res = await getProjectImage({ projectId: id });
        if (this.isBizError(res)) {
          this.applyProjectImageData(null);
          this.showMessage("error", res.msg || "项目图片加载失败");
          return;
        }
        this.applyProjectImageData(res && res.data);
      } catch (error) {
        this.applyProjectImageData(null);
        const msg =
          (error && error.msg) || error.message || "项目图片加载失败，请稍后重试";
        this.showMessage("error", msg);
      } finally {
        this.projectImageLoading = false;
      }
    },
    beforeProjectImageUpload(file) {
      const mime = String(file.type || "").toLowerCase();
      const name = String(file.name || "").toLowerCase();
      const isValidType =
        PROJECT_IMAGE_ACCEPT.includes(mime) ||
        /\.(jpe?g|png|gif)$/.test(name);
      if (!isValidType) {
        this.showMessage("warning", "仅支持 jpg / png / gif 格式图片");
        return false;
      }
      if (file.size > PROJECT_IMAGE_MAX_SIZE) {
        this.showMessage("warning", "图片大小不能超过 2MB");
        return false;
      }
      if (!this.editingProjectId) {
        this.showMessage("warning", "缺少项目ID，无法上传图片");
        return false;
      }
      return true;
    },
    async handleProjectImageUpload(option) {
      const file = option && option.file;
      if (!file || !this.editingProjectId) {
        if (option && typeof option.onError === "function") {
          option.onError(new Error("上传参数无效"));
        }
        return;
      }

      this.projectImageUploading = true;
      try {
        const res = await uploadProjectImage({
          file,
          projectId: this.editingProjectId,
        });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "图片上传失败");
          if (typeof option.onError === "function") {
            option.onError(new Error(res.msg || "图片上传失败"));
          }
          return;
        }
        const data = (res && res.data) || {};
        const imageUrl = String(data.imageUrl || "").trim();
        const fileName =
          String(data.fileName || file.name || "").trim() || "项目图片";
        const imageId = String(data.imageId || "").trim();
        if (!imageUrl) {
          this.showMessage("error", "上传成功但未返回图片地址");
          if (typeof option.onError === "function") {
            option.onError(new Error("未返回图片地址"));
          }
          return;
        }
        this.projectImageInfo = {
          imageId,
          projectId: String(data.projectId || this.editingProjectId),
          imageUrl,
          fileName,
        };
        this.projectImageFileList = [
          {
            name: fileName,
            url: imageUrl,
            uid: imageId || file.uid || `project-image-${Date.now()}`,
            status: "success",
          },
        ];
        this.resetProjectImageInlineTransform();
        this.showMessage("success", (res && res.msg) || "图片上传成功");
        if (typeof option.onSuccess === "function") {
          option.onSuccess(res);
        }
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "图片上传失败，请稍后重试";
        this.showMessage("error", msg);
        if (typeof option.onError === "function") {
          option.onError(error);
        }
      } finally {
        this.projectImageUploading = false;
      }
    },
    openProjectImagePreview() {
      const url = String(
        (this.projectImageInfo && this.projectImageInfo.imageUrl) || ""
      ).trim();
      if (!url) {
        this.showMessage("warning", "暂无可预览图片");
        return;
      }
      this.projectImagePreviewUrl = url;
      this.resetProjectImagePreviewTransform();
      this.projectImagePreviewVisible = true;
    },
    async removeProjectImage() {
      if (this.projectImageRemoving) return;
      const projectId = String(
        (this.projectImageInfo && this.projectImageInfo.projectId) ||
          this.editingProjectId ||
          ""
      ).trim();
      const imageId = String(
        (this.projectImageInfo && this.projectImageInfo.imageId) || ""
      ).trim();

      if (!projectId) {
        this.applyProjectImageData(null);
        return;
      }

      this.projectImageRemoving = true;
      try {
        const res = await deleteProjectImage({ projectId, imageId });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "图片删除失败");
          return;
        }
        this.applyProjectImageData(null);
        this.showMessage("success", (res && res.msg) || "图片已删除");
      } catch (error) {
        this.showMessage(
          "error",
          (error && error.msg) || error.message || "图片删除失败，请稍后重试"
        );
      } finally {
        this.projectImageRemoving = false;
      }
    },
    handleProjectImageExceed() {
      this.showMessage("warning", "只能上传一张图片，请先删除后再上传");
    },
    formatDurationDisplay(duration) {
      const text = duration != null ? String(duration).trim() : "";
      if (!text) return "";
      return /小时/.test(text) ? text : `${text}小时`;
    },
    mapDevicesFromChecked(checkedDevices) {
      if (!Array.isArray(checkedDevices)) return [];
      return checkedDevices
        .map((d) => {
          const name = (d && (d.deviceName || d.name || "")).trim();
          return name ? { name } : null;
        })
        .filter(Boolean);
    },
    mapProjectRows(list) {
      if (!Array.isArray(list)) return [];
      return list.map((item) => ({
        id: String(item.id ?? item.projectId ?? item.code ?? ""),
        code: item.code || "",
        name: item.name || "",
        serviceDescribe: item.serviceDescribe || item.describe || "",
        duration: item.duration || "",
        devices: this.mapDevicesFromChecked(item.checkedDevices),
      }));
    },
    async fetchProjectList() {
      this.listLoading = true;
      try {
        const res = await listProjectsWithDevices();
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "列表加载失败");
          this.rows = [];
          return;
        }
        this.rows = this.mapProjectRows(res && res.data);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "列表加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.rows = [];
      } finally {
        this.listLoading = false;
      }
    },
    resolveDeviceListPayload(data) {
      if (Array.isArray(data)) return data;
      if (!data || typeof data !== "object") return [];
      if (Array.isArray(data.records)) return data.records;
      if (Array.isArray(data.list)) return data.list;
      if (Array.isArray(data.rows)) return data.rows;
      return [];
    },
    normalizeDevice(device) {
      if (!device) return null;
      if (typeof device === "string") {
        return { deviceId: device, deviceName: device };
      }
      const deviceId = String(device.deviceId ?? device.id ?? "").trim();
      const deviceName = String(
        device.deviceName ?? device.name ?? ""
      ).trim();
      if (!deviceId || !deviceName) return null;
      return { deviceId, deviceName };
    },
    mapDevicesFromApi(data) {
      const list = this.resolveDeviceListPayload(data);
      return list.map((item) => this.normalizeDevice(item)).filter(Boolean);
    },
    resolveRepairListPayload(data) {
      if (Array.isArray(data)) return data;
      if (!data || typeof data !== "object") return [];
      if (Array.isArray(data.records)) return data.records;
      if (Array.isArray(data.list)) return data.list;
      if (Array.isArray(data.rows)) return data.rows;
      return [];
    },
    normalizeRepair(repair) {
      if (!repair) return null;
      if (typeof repair === "string") {
        return { repairId: repair, repairName: repair };
      }
      const repairId = String(repair.repairId ?? repair.id ?? "").trim();
      const repairName = String(repair.repairName ?? repair.name ?? "").trim();
      if (!repairId || !repairName) return null;
      return { repairId, repairName };
    },
    mapRepairsFromApi(data) {
      const list = this.resolveRepairListPayload(data);
      return list.map((item) => this.normalizeRepair(item)).filter(Boolean);
    },
    async fetchAllRepairs() {
      this.repairsLoading = true;
      try {
        const res = await listAllRepairs();
        if (this.isBizError(res)) {
          this.allRepairs = [];
          this.showMessage("error", res.msg || "修复产品列表加载失败");
          return;
        }
        this.allRepairs = this.mapRepairsFromApi(res && res.data);
      } catch (e) {
        this.allRepairs = [];
        const msg = (e && e.msg) || e.message || "修复产品列表加载失败";
        this.showMessage("error", msg);
      } finally {
        this.repairsLoading = false;
      }
    },
    async fetchAllDevices() {
      this.devicesLoading = true;
      try {
        const res = await listAllDevices();
        if (this.isBizError(res)) {
          this.allDevices = [];
          this.showMessage("error", res.msg || "设备列表加载失败");
          return;
        }
        this.allDevices = this.mapDevicesFromApi(res && res.data);
      } catch (e) {
        this.allDevices = [];
        const msg = (e && e.msg) || e.message || "设备列表加载失败";
        this.showMessage("error", msg);
      } finally {
        this.devicesLoading = false;
      }
    },
    stripDurationSuffix(duration) {
      return String(duration ?? "")
        .replace(/小时\s*$/, "")
        .trim();
    },
    extractCheckedDeviceIds(checkedDevices) {
      if (!Array.isArray(checkedDevices)) return [];
      return checkedDevices
        .map((d) => String(d.deviceId ?? d.id ?? "").trim())
        .filter(Boolean);
    },
    extractRepairIds(repairPayload) {
      if (!Array.isArray(repairPayload)) return [];
      return repairPayload
        .map((item) => {
          if (typeof item === "string" || typeof item === "number") {
            return String(item).trim();
          }
          return String(item.repairId ?? item.id ?? "").trim();
        })
        .filter(Boolean);
    },
    matchRepairIdsToCatalog(repairIds) {
      const ids = (repairIds || []).map((id) => String(id).trim()).filter(Boolean);
      if (!this.allRepairs.length) return ids;
      return ids.map((id) => {
        const matched = this.allRepairs.find(
          (repair) => String(repair.repairId) === id
        );
        return matched ? matched.repairId : id;
      });
    },
    fillFormFromProject(data) {
      if (!data) return;
      const checkedDeviceIds = this.matchDeviceIdsToCatalog(
        this.extractCheckedDeviceIds(data.checkedDevices)
      );
      const checkedRepairIds = this.matchRepairIdsToCatalog(
        this.extractRepairIds(data.repairIds)
      );
      this.projectForm = {
        code: data.code || "",
        name: data.name || "",
        describe: data.serviceDescribe || data.describe || "",
        duration: this.stripDurationSuffix(data.duration),
        isFieldRequired: Number(data.isFieldRequired) === 1 ? 1 : 0,
        checkedDeviceIds,
        checkedRepairIds,
      };
      this.editingProjectId = String(data.id ?? data.projectId ?? "");
    },
    matchDeviceIdsToCatalog(deviceIds) {
      const ids = (deviceIds || []).map((id) => String(id).trim()).filter(Boolean);
      if (!this.allDevices.length) return ids;
      return ids.map((id) => {
        const matched = this.allDevices.find(
          (device) => String(device.deviceId) === id
        );
        return matched ? matched.deviceId : id;
      });
    },
    openAddModal() {
      this.modalMode = "add";
      this.editingProjectId = null;
      this.projectDetailLoading = false;
      this.resetProjectForm();
      this.showProjectModal = true;
      this.fetchAllDevices();
      this.fetchAllRepairs();
    },
    async editRow(item) {
      if (!item || !item.id) {
        this.showMessage("warning", "项目信息无效");
        return;
      }

      this.modalMode = "edit";
      this.editingProjectId = String(item.id);
      this.projectDetailLoading = true;
      this.resetProjectForm();
      this.showProjectModal = true;

      await Promise.all([this.fetchAllDevices(), this.fetchAllRepairs()]);

      try {
        const res = await getProject({ projectId: item.id });
        if (this.isBizError(res)) {
          this.showMessage("error", res.msg || "项目详情加载失败");
          this.closeProjectModal();
          return;
        }
        const detail = (res && res.data) != null ? res.data : res;
        this.fillFormFromProject(detail);
        await this.fetchProjectImage(item.id);
      } catch (error) {
        const msg =
          (error && error.msg) || error.message || "项目详情加载失败，请稍后重试";
        this.showMessage("error", msg);
        this.closeProjectModal();
      } finally {
        this.projectDetailLoading = false;
      }
    },
    closeProjectModal() {
      this.showProjectModal = false;
      this.showRepairModal = false;
      this.modalMode = "add";
      this.editingProjectId = null;
      this.projectDetailLoading = false;
      this.resetProjectImage();
    },
    async openRepairModal() {
      this.showRepairModal = true;
      if (!this.allRepairs.length) {
        await this.fetchAllRepairs();
      }
      this.projectForm.checkedRepairIds = this.matchRepairIdsToCatalog(
        this.projectForm.checkedRepairIds
      );
    },
    closeRepairModal() {
      this.showRepairModal = false;
    },
    saveRepairModal() {
      this.projectForm.checkedRepairIds = this.matchRepairIdsToCatalog(
        this.projectForm.checkedRepairIds
      );
      this.closeRepairModal();
    },
    buildCheckedDevicesForSubmit(checkedDeviceIds) {
      return (checkedDeviceIds || []).map((id) => {
        const deviceId = String(id);
        const device = this.allDevices.find((d) => d.deviceId === deviceId);
        return {
          deviceId,
          deviceName: device ? device.deviceName : "",
        };
      });
    },
    async saveProject() {
      const {
        code,
        name,
        describe,
        duration,
        isFieldRequired,
        checkedDeviceIds,
        checkedRepairIds,
      } = this.projectForm;
      if (!code || !name) {
        this.showMessage("warning", "请填写项目编号和项目名称");
        return;
      }
      if (!describe) {
        this.showMessage("warning", "请填写项目描述");
        return;
      }
      if (!duration) {
        this.showMessage("warning", "请填写预计耗时");
        return;
      }

      const checkedDevices = this.buildCheckedDevicesForSubmit(
        checkedDeviceIds.map((id) => String(id))
      );
      const repairIds = (checkedRepairIds || []).map((id) => String(id));
      const isEdit = this.modalMode === "edit";

      if (isEdit && !this.editingProjectId) {
        this.showMessage("warning", "项目信息无效，无法保存");
        return;
      }

      this.savingProject = true;
      try {
        const res = isEdit
          ? await updateProject({
              id: this.editingProjectId,
              code,
              name,
              duration,
              serviceDescribe: describe,
              isFieldRequired: Number(isFieldRequired) === 1 ? 1 : 0,
              checkedDevices,
              repairIds,
            })
          : await addProjects({
              code,
              name,
              serviceDescribe: describe,
              duration,
              isFieldRequired: Number(isFieldRequired) === 1 ? 1 : 0,
              deviceIds: checkedDevices.map((d) => d.deviceId),
              repairIds,
            });

        if (this.isBizError(res)) {
          this.showMessage(
            "error",
            res.msg || (isEdit ? "项目更新失败" : "项目新增失败")
          );
          return;
        }

        this.showMessage(
          "success",
          (res && res.msg) || (isEdit ? "项目更新成功" : "项目新增成功")
        );
        this.closeProjectModal();
        await this.fetchProjectList();
      } catch (error) {
        const msg =
          (error && error.msg) ||
          error.message ||
          (isEdit ? "项目更新失败，请稍后重试" : "项目新增失败，请稍后重试");
        this.showMessage("error", msg);
      } finally {
        this.savingProject = false;
      }
    },
    deleteRow(item) {
      if (!item || !item.id) {
        this.showMessage("warning", "项目信息无效");
        return;
      }

      const label =
        item.code && item.name
          ? `「(${item.code})${item.name}」`
          : item.name
            ? `「${item.name}」`
            : "";

      this.$confirm(`确认删除项目${label}？`, "提示", {
        confirmButtonText: "确定",
        cancelButtonText: "取消",
        type: "warning",
      })
        .then(async () => {
          try {
            const res = await deleteProject({ projectId: item.id });
            if (this.isBizError(res)) {
              this.showMessage("error", res.msg || "项目删除失败");
              return;
            }
            this.showMessage("success", (res && res.msg) || "项目已删除");
            await this.fetchProjectList();
          } catch (error) {
            const msg =
              (error && error.msg) ||
              error.message ||
              "项目删除失败，请稍后重试";
            this.showMessage("error", msg);
          }
        })
        .catch(() => {});
    },
  },
};
</script>

<style scoped>
.page-card {
  background: #fff;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 18px;
  color: #333;
  margin: 0;
  font-weight: 600;
}

.list-loading,
.empty-cell {
  padding: 40px 0;
  text-align: center;
  color: #999;
  font-size: 14px;
}

.section-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}

.add-btn {
  border: none;
  background: #409eff;
  color: #fff;
  border-radius: 4px;
  height: 32px;
  padding: 0 14px;
  font-size: 14px;
  cursor: pointer;
}

.add-btn:hover {
  background: #096dd9;
}

.service-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
  border: 1px solid #eef1f6;
}

.service-table th,
.service-table td {
  border-bottom: 1px solid #eef1f6;
  padding: 14px 16px;
  font-size: 14px;
  color: #3f4a5d;
  vertical-align: middle;
  text-align: left;
}

.service-table th {
  background: #f8f9fc;
  color: #616f84;
  font-weight: 600;
}

.service-table tbody tr:hover td {
  background: #f0f7ff;
}

.service-table .col-code {
  width: 72px;
}

.service-table .col-duration {
  width: 100px;
  white-space: nowrap;
}

.service-table .col-devices {
  min-width: 280px;
}

.service-table .col-action {
  width: 140px;
  text-align: center;
}

.cell-code {
  font-weight: 700;
  color: #2f3a4c;
}

.cell-name {
  font-weight: 500;
  color: #2f3a4c;
  white-space: nowrap;
}

.cell-desc {
  color: #5c6b7f;
  line-height: 1.5;
}

.device-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.device-tag {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.4;
  white-space: nowrap;
}

.device-tag--highlight {
  color: #389e0d;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
}

.action-btns {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.action-btn {
  border-radius: 4px;
  padding: 5px 14px;
  font-size: 13px;
  cursor: pointer;
  white-space: nowrap;
}

.action-btn-edit {
  background: #fff;
  color: #5c6b7f;
  border: 1px solid #d9d9d9;
}

.action-btn-edit:hover {
  color: #409eff;
  border-color: #409eff;
}

.action-btn-delete {
  background: #ff4d4f;
  color: #fff;
  border: 1px solid #ff4d4f;
}

.action-btn-delete:hover {
  background: #ff7875;
  border-color: #ff7875;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
}

.modal-service {
  width: 640px;
  max-width: calc(100% - 32px);
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}

.modal-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  cursor: pointer;
  font-size: 20px;
  color: #999;
  line-height: 1;
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.modal-detail-loading {
  padding: 32px 0;
  text-align: center;
  color: #666;
  font-size: 14px;
}

.form-row {
  display: flex;
  gap: 16px;
}

.form-row-2 .form-group {
  flex: 1;
  min-width: 0;
}

.form-group {
  margin-bottom: 16px;
}

.form-group:last-child {
  margin-bottom: 0;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #333;
}

.form-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  font-size: 14px;
  box-sizing: border-box;
  color: #333;
}

.form-input:focus {
  outline: none;
  border-color: #409eff;
  box-shadow: 0 0 0 2px rgba(64, 158, 255, 0.15);
}

.form-input::placeholder {
  color: #bfbfbf;
}

.duration-input-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}

.duration-input {
  width: 160px;
  flex-shrink: 0;
}

.duration-suffix {
  font-size: 14px;
  color: #333;
  line-height: 32px;
  white-space: nowrap;
}

.field-required-switch-group .form-label {
  margin-bottom: 8px;
}

.field-required-switch-row {
  line-height: 1;
}

.device-check-loading,
.device-check-empty {
  font-size: 13px;
  color: #999;
  padding: 4px 0;
}

.device-check-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
}

.device-check-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  user-select: none;
}

.device-check-item input {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #409eff;
}

.repair-link-btn {
  display: inline-block;
  margin-top: 10px;
  padding: 0;
  border: none;
  background: none;
  color: #409eff;
  font-size: 14px;
  cursor: pointer;
  line-height: 1.5;
}

.repair-link-btn:hover {
  color: #096dd9;
}

.repair-selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}

.repair-tag {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 13px;
  line-height: 1.5;
  color: #1890ff;
  background: #e6f7ff;
  border: 1px solid #91d5ff;
}

.repair-modal-overlay {
  z-index: 1100;
}

.modal-repair-select {
  width: 520px;
  max-width: calc(100% - 32px);
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.repair-check-loading,
.repair-check-empty {
  font-size: 13px;
  color: #999;
  padding: 4px 0;
}

.repair-check-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px 24px;
}

.repair-check-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: #333;
  cursor: pointer;
  user-select: none;
}

.repair-check-item input {
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
  accent-color: #409eff;
}

.modal-footer {
  padding: 12px 20px 16px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.project-image-group {
  margin-top: 4px;
}

.project-image-loading {
  padding: 16px 0;
  color: #909399;
  font-size: 13px;
}

.project-image-upload >>> .el-upload--picture-card {
  width: 100px;
  height: 100px;
  line-height: 108px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  background: #fff;
}

.project-image-upload >>> .el-upload--picture-card:hover {
  border-color: #409eff;
}

.project-image-viewer {
  display: inline-block;
}

.project-image-thumb {
  position: relative;
  width: 100px;
  height: 100px;
  border: 1px solid #c0ccda;
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
}

.project-image-thumb-img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.project-image-thumb-mask {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity 0.15s;
  color: #fff;
  font-size: 20px;
}

.project-image-thumb:hover .project-image-thumb-mask {
  opacity: 1;
}

.project-image-thumb-mask i {
  cursor: pointer;
}

.project-image-tip {
  margin-top: 8px;
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
}

.project-image-preview-tip {
  margin-bottom: 8px;
  font-size: 12px;
  color: #909399;
  text-align: center;
}

.project-image-preview-stage {
  width: 100%;
  height: 65vh;
  overflow: hidden;
  background: #f5f5f5;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  user-select: none;
}

.project-image-preview {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  transform-origin: center center;
}

.footer-btn {
  min-width: 72px;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
}

.footer-btn-cancel {
  background: #fff;
  color: #333;
  border: 1px solid #d9d9d9;
}

.footer-btn-cancel:hover {
  color: #409eff;
  border-color: #409eff;
}

.footer-btn-save {
  background: #409eff;
  color: #fff;
  border: 1px solid #409eff;
}

.footer-btn-save:hover {
  background: #096dd9;
  border-color: #096dd9;
}

.footer-btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
</style>
